'use client';

import clsx from 'clsx';
import React, { useActionState, useEffect, useMemo, useRef, useState, startTransition } from 'react';
import { INPUT_STATE_DEFAULT, INPUT_STATE_ERROR } from '@pixum/auth-core-ui';
import Button, { ButtonVariants } from '@pixum/button';
import { Icon } from '@pixum/icon';
import Dropdown, { type DropdownOption } from '@pixum/dropdown';
import { TextInput, EmailInput } from '@pixum/web-input';
import { analytics } from '@pixum/tracking';
import { formFieldState } from '@pixum/utils';
import type { ContactFormProps, FormFieldsType, FormFieldKey } from './types';
import { submitContactFormAction, type ContactFormActionState } from '../../actions/submitContactFormAction';
import { CancellationLoginBanner } from '../cancellation-fields/partials/partials/CancellationLoginBanner';
import { useUIStore } from 'stores/ui';
import { PixumEnvUtils, logger, withTimeout } from 'utils';
import { ensureSession } from 'services/session/client';
import { SESSION_REQUEST_TIMEOUT_MS } from 'hooks';
import { CANCELLATION_TOPIC_KEYS } from 'data-layer/constants';
import styles from './ContactForm.module.css';

const FORM_FIELDS_STATE_DEFAULT: FormFieldsType = {
  fromName: '',
  fromEMail: '',
  subject: '',
  text: ''
};

const TRACKING = {
  category: 'Contact-Form-Tabs',
  action: 'Kontakt-Layer',
  option: 'Kontakt E-Mail'
};

const TRACKING_SERVICE = 'service';
const TRACKING_EMAIL = 'e-mail';

export function ContactForm({
  device,
  formConstants,
  topics,
  cancellationSlot,
  isAuthenticated,
  isAuthLoading,
  onCancel,
  onSubmitSuccess,
  className,
  locale,
  composeLabels,
  formRef
}: ContactFormProps) {
  const { contactForm, setContactFormFields, setCancellationErrors } = useUIStore(state => state);
  const isMobile = device === 'mobile';

  const [formState, setFormState] = useState<FormFieldsType>(FORM_FIELDS_STATE_DEFAULT);
  const [formErrors, setFormErrors] = useState<FormFieldsType>(FORM_FIELDS_STATE_DEFAULT);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const onSubmitSuccessRef = useRef(onSubmitSuccess);

  useEffect(() => {
    onSubmitSuccessRef.current = onSubmitSuccess;
  }, [onSubmitSuccess]);

  const {
    nameLabel,
    emailLabel,
    subjectLabel,
    textLabel,
    cancelLabel,
    sendLabel,
    chooseYourTopic,
    cancelContractLabel,
    emailEmptyErrorLabel,
    emailInvalidErrorLabel,
    cancelOrderRequiredErrorLabel,
    cancelProductsRequiredErrorLabel,
    cancelProductsPerOrderRequiredErrorLabel,
  } = formConstants;

  const boundAction = submitContactFormAction.bind(
    null,
    locale,
    composeLabels,
    emailEmptyErrorLabel,
    emailInvalidErrorLabel,
    cancelOrderRequiredErrorLabel,
    cancelProductsRequiredErrorLabel,
    cancelProductsPerOrderRequiredErrorLabel
  );
  const [state, formAction, isPending] = useActionState<ContactFormActionState, FormData>(boundAction, null);

  const contentAreaRows = useMemo(() => (isMobile ? 5 : 10), [isMobile]);

  const isCancellationTopic = CANCELLATION_TOPIC_KEYS.includes(String(contactForm.selectedSubject?.id ?? ''));
  const isFormDisabled = isCancellationTopic && !isAuthenticated && !isAuthLoading;

  useEffect(() => {
    if (!state) return;

    if (state.success) {
      setCancellationErrors({});
      analytics.trackEvent(TRACKING.category, TRACKING.action, TRACKING.option);
      onSubmitSuccessRef.current?.();
    } else {
      analytics.trackEvent(TRACKING_SERVICE, TRACKING_EMAIL, 'Pflicht');

      if (state.fieldErrors) {
        const fieldMap: Partial<Record<keyof FormFieldsType, string>> = {
          fromName: 'fromName',
          fromEMail: 'fromEMail',
          subject: 'subject',
          text: 'text'
        };

        setFormState(prev => {
          const next = { ...prev };

          for (const [field, key] of Object.entries(fieldMap)) {
            if (state.fieldErrors[field]?.length) {
              next[key as keyof FormFieldsType] = INPUT_STATE_ERROR;
            }
          }

          return next;
        });

        setFormErrors(prev => {
          const next = { ...prev };

          for (const [field, key] of Object.entries(fieldMap)) {
            const msg = state.fieldErrors[field]?.[0];

            if (msg) next[key as keyof FormFieldsType] = msg;
          }

          return next;
        });

        setCancellationErrors({
          orderError: state.fieldErrors['canceledOrderId']?.[0],
          productsError: state.fieldErrors['canceledProducts']?.[0]
        });
      }
    }
  }, [state]);

  const handleSelectOption = (option: DropdownOption) => {
    setFormState({ ...formState, subject: INPUT_STATE_DEFAULT });
    setFormErrors({ ...formErrors, subject: '' });
    setContactFormFields({ selectedSubject: option });
    analytics.trackEvent(TRACKING_SERVICE, TRACKING_EMAIL, option.text);

    const selectedTopic = topics.find(t => t.internalName === String(option.id));

    setContactFormFields({ text: selectedTopic?.prefilledMessage ?? '' });
    setFormState(prev => ({ ...prev, text: INPUT_STATE_DEFAULT }));
    setFormErrors(prev => ({ ...prev, text: '' }));
    setCancellationErrors({});

    if (CANCELLATION_TOPIC_KEYS.includes(String(option.id))) {
      const user = PixumEnvUtils.get('USER');
      const fromName = user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : '';
      const fromEMail = user?.email ?? '';

      setContactFormFields({ fromName, fromEMail });
      setFormState(prev => ({ ...prev, fromName: INPUT_STATE_DEFAULT, fromEMail: INPUT_STATE_DEFAULT }));
      setFormErrors(prev => ({ ...prev, fromName: '', fromEMail: '' }));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContactFormFields({ [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Enter in a field submits natively even while the button is busy.
    if (isSubmitting || isPending) return;

    analytics.trackEvent(TRACKING_SERVICE, TRACKING_EMAIL, 'Klick auf senden');

    const formData = new FormData(e.currentTarget);
    formData.set('fromName', String(contactForm.fromName));
    formData.set('fromEMail', String(contactForm.fromEMail));
    formData.set('subject', contactForm.selectedSubject?.label || '');
    formData.set('topicInternalName', String(contactForm.selectedSubject?.id || ''));
    formData.set('text', String(contactForm.text));

    setIsSubmitting(true);

    try {
      try {
        // The action reads the session cookie, so the browser session must exist before it runs.
        await withTimeout(ensureSession(), SESSION_REQUEST_TIMEOUT_MS);
      } catch (error) {
        logger.warn({ error }, '[ContactForm] Could not create a session before submit, submitting anyway');
      }

      // The await stays outside the transition so useActionState still dispatches inside one.
      startTransition(() => formAction(formData));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormState(FORM_FIELDS_STATE_DEFAULT);
    setFormErrors(FORM_FIELDS_STATE_DEFAULT);
    setCancellationErrors({});

    if (onCancel) {
      onCancel();
    }
  };

  const handleOnFocus = (fieldName: FormFieldKey) => {
    analytics.trackEvent(TRACKING_SERVICE, TRACKING_EMAIL, 'Email form opened');
    setFormState(prev => ({ ...prev, [fieldName]: INPUT_STATE_DEFAULT }));
  };

  useEffect(() => {
    const prefillFromUser = () => {
      const user = PixumEnvUtils.get('USER');
      const userName = user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : '';
      const userEmail = user?.email || '';

      if (!contactForm.fromName && userName) {
        setContactFormFields({ fromName: userName });
        setFormState(prev => ({ ...prev, fromName: INPUT_STATE_DEFAULT }));
        setFormErrors(prev => ({ ...prev, fromName: '' }));
      }

      if (!contactForm.fromEMail && userEmail) {
        setContactFormFields({ fromEMail: userEmail });
        setFormState(prev => ({ ...prev, fromEMail: INPUT_STATE_DEFAULT }));
        setFormErrors(prev => ({ ...prev, fromEMail: '' }));
      }
    };

    const cleanupOnce = PixumEnvUtils.onceExtended(prefillFromUser);
    const cleanupPersistent = PixumEnvUtils.subscribeExtended(prefillFromUser);

    return () => {
      cleanupOnce();
      cleanupPersistent();
    };
  }, []);

  useEffect(() => {
    const availableSubjects: DropdownOption[] = topics.map(topic => ({
      id: topic.internalName,
      label: topic.label,
      text: topic.label
    }));

    setContactFormFields({ availableSubjects });
  }, [topics]);

  return (
    <form ref={formRef} tabIndex={-1} className={clsx(styles.form, className)} onSubmit={handleSubmit}>
      {isCancellationTopic && !isAuthenticated && !isAuthLoading && (
        <CancellationLoginBanner constants={formConstants} />
      )}
      <div className={styles.fields}>
        <TextInput
          disabled={isCancellationTopic}
          loading={isAuthLoading}
          hasError={formState.fromName === INPUT_STATE_ERROR}
          inputInfo={formErrors.fromName}
          label={nameLabel}
          name="fromName"
          onChange={handleChange}
          onFocus={() => handleOnFocus('fromName')}
          value={contactForm.fromName}
          required
        />
        <EmailInput
          disabled={isCancellationTopic}
          loading={isAuthLoading}
          autoComplete="email"
          hasError={formState.fromEMail === INPUT_STATE_ERROR}
          inputInfo={formErrors.fromEMail}
          label={emailLabel}
          name="fromEMail"
          onChange={handleChange}
          onFocus={() => handleOnFocus('fromEMail')}
          value={contactForm.fromEMail}
          required
        />
      </div>
      <Dropdown
        hasError={formState.subject === INPUT_STATE_ERROR}
        helperText={formErrors.subject}
        label={subjectLabel}
        name="subject"
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onChange={() => {}}
        onOptionClick={option => handleSelectOption(option)}
        options={contactForm.availableSubjects || []}
        placeholder={chooseYourTopic}
        selectedOption={contactForm.selectedSubject}
        state={formState.subject === INPUT_STATE_ERROR ? formFieldState.error : formFieldState.default}
        value={contactForm.selectedSubject?.label || ''}
      />
      {isCancellationTopic && <div className={styles.cancellationSlotVisible}>{cancellationSlot}</div>}
      <TextInput
        multiline
        disabled={isFormDisabled}
        loading={isAuthLoading}
        hasError={formState.text === INPUT_STATE_ERROR}
        inputInfo={formErrors.text}
        label={textLabel}
        name="text"
        onChange={handleChange}
        onFocus={() => handleOnFocus('text')}
        rows={contentAreaRows}
        value={contactForm.text}
      />
      <div className={styles.actions}>
        <Button
          className={styles.cancelButton}
          onClick={handleCancel}
          label={cancelLabel}
          variant={ButtonVariants.SECONDARY}
          type="button"
        >
          <Icon name="close" />
          <span>{cancelLabel}</span>
        </Button>
        <Button
          disabled={isFormDisabled}
          isBusy={isPending || isAuthLoading || isSubmitting}
          label={isCancellationTopic ? cancelContractLabel : sendLabel}
          type="submit"
          variant={ButtonVariants.PRIMARY}
        >
          <Icon name="send" color="var(--palette-ink-0)" />
          <span>{isCancellationTopic ? cancelContractLabel : sendLabel}</span>
        </Button>
      </div>
    </form>
  );
}
